import { useState, useEffect } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from '@heroicons/react/24/solid'

const testimonials = [
  {
    quote: 'The accountability rituals gave me a calmer way to stay consistent with the routines I chose.',
    author: 'Shannon M.',
    date: 'Nov 24, 2024',
    image: '/images/testimonial-shannon.webp',
    product: 'Accountability support',
    metric: 'Routine consistency',
  },
  {
    quote: 'Having someone to check in with helped me make space for the habits I wanted to keep.',
    author: 'Tara S.',
    date: 'Nov 20, 2024',
    image: '/images/testimonial-tara.webp',
    product: 'Lifestyle support',
    metric: 'More daily structure',
  },
  {
    quote: 'The process felt more manageable when I could reflect on progress instead of chasing perfection.',
    author: 'Emily C.',
    date: 'Nov 23, 2024',
    image: '/images/testimonial-emily.webp',
    product: 'Remote check-ins',
    metric: 'Steady progress',
  },
  {
    quote: 'The support helped me stay connected to my goals when life got busy.',
    author: 'Jessica L.',
    date: 'Nov 1, 2024',
    image: '/images/testimonial-jessica.webp',
    product: 'Routine support',
    metric: 'More confidence',
  },
  {
    quote: 'I finally have a simple rhythm I can return to, even after an off week.',
    author: 'Rhonda L.',
    date: 'Nov 28, 2024',
    image: '/images/testimonial-rhonda.webp',
    product: 'Accountability support',
    metric: 'A sustainable rhythm',
  },
]

const Results = () => {
  const total = testimonials.length
  const [[page, direction], setPage] = useState<[number, number]>([0, 0])
  const [isHovering, setIsHovering] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const prefersReducedMotion = useReducedMotion() ?? false

  useEffect(() => {
    if (isHovering || isFocused || isPaused || prefersReducedMotion) return
    const intervalId = setInterval(() => {
      setPage(([index]) => {
        const next = (index + 1) % total
        return [next, 1]
      })
    }, 5000)
    return () => clearInterval(intervalId)
  }, [total, isHovering, isFocused, isPaused, prefersReducedMotion])

  const goToPrevious = () => {
    setPage(([index]) => {
      const prev = (index - 1 + total) % total
      return [prev, -1]
    })
  }

  const goToNext = () => {
    setPage(([index]) => {
      const next = (index + 1) % total
      return [next, 1]
    })
  }

  const variants = prefersReducedMotion
    ? {
        enter: { opacity: 0 },
        center: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        enter: (dir: number) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir: number) => ({ x: dir < 0 ? 120 : -120, opacity: 0 }),
      }

  const currentTestimonial = testimonials[page]
  const progressPercentage = ((page + 1) / total) * 100
  const toggleAutoPlay = () => {
    setIsPaused((prev) => !prev)
    setIsFocused(false)
    setIsHovering(false)
  }

  return (
    <section className="relative isolate overflow-hidden bg-[#cdfbf1] py-24 text-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="absolute inset-y-0 left-10 w-px bg-gradient-to-b from-transparent via-[#8be4d2]/60 to-transparent" />

      <div className="container relative z-10 mx-auto px-6">
        <div className="grid gap-16 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="space-y-8">
            <p className="text-xs uppercase tracking-[0.6em] text-teal-700">Proof of calm progress</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900">Real people, practical routines</h2>
            <p className="text-base text-slate-700 leading-relaxed">
              These examples reflect the kinds of personal routine and accountability wins people pursue with Andrea's non-medical support. Individual experiences vary, and clinical decisions remain with each person's licensed care team.
            </p>
            <div className="rounded-2xl border border-teal-100 bg-white/80 p-5 text-sm text-slate-600 shadow-[0_10px_25px_rgba(6,96,94,0.08)]">
              No fabricated metrics here. Just lived experiences that speak for themselves.
            </div>
          </div>

          <div
            className="relative"
            role="region"
            aria-roledescription="carousel"
            aria-label="Member testimonials"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onFocusCapture={() => setIsFocused(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsFocused(false)
            }}
          >
            <div className="rounded-[32px] border border-white/80 bg-white/90 p-1 shadow-[0_25px_60px_rgba(6,96,94,0.15)]">
              <motion.div
                layout
                transition={{ layout: { duration: prefersReducedMotion ? 0 : 0.35, ease: 'easeOut' } }}
                className="relative overflow-hidden rounded-[28px] bg-white p-8"
              >
                <div className="absolute right-6 top-6 h-20 w-20 rounded-full bg-[#cdfbf1] opacity-60 blur-3xl" />
                <div className="grid min-w-0">
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={`sizer-${testimonial.author}`}
                      aria-hidden="true"
                      className="invisible pointer-events-none col-start-1 row-start-1 flex min-w-0 flex-col gap-6"
                    >
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="block h-16 w-16 rounded-2xl lg:h-20 lg:w-20" />
                        <div>
                          <p className="text-lg font-semibold text-slate-900 lg:text-2xl">{testimonial.author}</p>
                          <p className="text-sm text-slate-500 lg:text-lg">{testimonial.date}</p>
                        </div>
                        <span className="ml-auto max-w-full rounded-full bg-[#cdfbf1] px-4 py-1 text-xs font-semibold text-teal-800 lg:px-5 lg:py-1.5 lg:text-base">
                          {testimonial.product}
                        </span>
                      </div>
                      <p className="text-xl italic leading-relaxed text-slate-700 lg:text-3xl lg:leading-[1.5]">"{testimonial.quote}"</p>
                      <div className="flex items-center justify-between text-sm text-slate-500 lg:text-base">
                        <span>{testimonial.metric}</span>
                        <span>
                          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="col-start-1 row-start-1 min-w-0">
                    <AnimatePresence mode="wait" custom={direction}>
                      <motion.div
                        key={page}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: prefersReducedMotion ? 0 : 0.5, ease: 'easeOut' }}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`Testimonial ${page + 1} of ${total}`}
                        className="relative flex flex-col gap-6"
                      >
                        <div className="flex flex-wrap items-center gap-4">
                          <img src={currentTestimonial.image} alt="" className="h-16 w-16 rounded-2xl object-cover lg:h-20 lg:w-20" />
                          <div>
                            <p className="text-lg font-semibold text-slate-900 lg:text-2xl">{currentTestimonial.author}</p>
                            <p className="text-sm text-slate-500 lg:text-lg">{currentTestimonial.date}</p>
                          </div>
                          <span className="ml-auto max-w-full rounded-full bg-[#cdfbf1] px-4 py-1 text-xs font-semibold text-teal-800 lg:px-5 lg:py-1.5 lg:text-base">
                            {currentTestimonial.product}
                          </span>
                        </div>
                        <p className="text-xl italic leading-relaxed text-slate-700 lg:text-3xl lg:leading-[1.5]">"{currentTestimonial.quote}"</p>
                        <div className="flex items-center justify-between text-sm text-slate-500 lg:text-base">
                          <span>{currentTestimonial.metric}</span>
                          <span>
                            {String(page + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                          </span>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <p className="sr-only" aria-live="polite" aria-atomic="true">
                  Testimonial {page + 1} of {total}: {currentTestimonial.author}. {currentTestimonial.quote}
                </p>

                <div
                  className="mt-8 h-1 w-full overflow-hidden rounded-full bg-slate-200"
                  role="progressbar"
                  aria-label={`Testimonial ${page + 1} of ${total}`}
                  aria-valuemin={1}
                  aria-valuemax={total}
                  aria-valuenow={page + 1}
                >
                  <motion.div
                    key={progressPercentage}
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
                    className="h-full rounded-full bg-gradient-to-r from-teal-400 to-cyan-300"
                  />
                </div>
              </motion.div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-6">
              <button
                type="button"
                onClick={goToPrevious}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-teal-100 bg-white text-teal-700 transition hover:bg-[#cdfbf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                aria-label="Previous testimonial"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
              {!prefersReducedMotion && (
                <button
                  type="button"
                  onClick={toggleAutoPlay}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-teal-100 bg-white text-teal-700 transition hover:bg-[#cdfbf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                  aria-label={isPaused ? 'Resume automatic testimonials' : 'Pause automatic testimonials'}
                  aria-pressed={isPaused}
                >
                  {isPaused ? <PlayIcon className="h-5 w-5" /> : <PauseIcon className="h-5 w-5" />}
                </button>
              )}
              <button
                type="button"
                onClick={goToNext}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-teal-100 bg-white text-teal-700 transition hover:bg-[#cdfbf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                aria-label="Next testimonial"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Results
